<section class="user-invite-page">
  <header class="user-invite-page__header">
    <div class="user-invite-page__header-inner">
      <div class="user-invite-page__header-left">
        <button
          type="button"
          class="user-invite-page__back"
          (click)="navigateBack()"
          [attr.aria-label]="'userInvite.header.back' | translate"
        >
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
        </button>
        <div class="user-invite-page__header-text">
          <div class="user-invite-page__eyebrow">{{ 'userInvite.header.eyebrow' | translate }}</div>
          <h1 class="user-invite-page__title">{{ 'userInvite.header.title' | translate }}</h1>
        </div>
      </div>
      <div class="user-invite-page__header-actions">
        <button type="button" class="user-invite-page__cancel" (click)="navigateBack()">
          {{ 'userInvite.header.cancel' | translate }}
        </button>
        <button type="button" class="user-invite-page__save" [disabled]="!canSubmit()" (click)="onSubmit()">
          <mat-icon aria-hidden="true">check</mat-icon>
          <span>{{ 'userInvite.submit' | translate }}</span>
        </button>
      </div>
    </div>
  </header>

  <div class="user-invite-page__body">
    <article class="user-invite-page__preview-card" aria-labelledby="user-invite-preview-title">
      <app-avatar [name]="previewName() || ('userInvite.preview.fallbackName' | translate)" [size]="68" />
      <div class="user-invite-page__preview-text">
        <div class="user-invite-page__eyebrow">{{ 'userInvite.preview.label' | translate }}</div>
        <div id="user-invite-preview-title" class="user-invite-page__preview-name">
          {{ previewName() || ('userInvite.preview.fallbackName' | translate) }}
        </div>
        <div class="user-invite-page__preview-meta">
          <span class="user-invite-page__preview-email">
            {{ previewEmail() || ('userInvite.preview.fallbackEmail' | translate) }}
          </span>
          @if (selectedRoleCode()) {
            <span class="user-invite-page__preview-dot" aria-hidden="true"></span>
            <app-role-badge [role]="selectedRoleCode()" />
          }
          <span class="user-invite-page__preview-dot" aria-hidden="true"></span>
          <app-status-pill kind="pending" [label]="statusDisplay | translate" size="sm" />
        </div>
      </div>
    </article>

    <div class="user-invite-page__tabs" role="tablist" [attr.aria-label]="'userInvite.tabs.aria' | translate">
      @for (tab of tabs; track tab.id) {
        <button
          type="button"
          role="tab"
          class="user-invite-page__tab"
          [class.user-invite-page__tab--active]="activeTab() === tab.id"
          [attr.aria-selected]="activeTab() === tab.id"
          (click)="setTab(tab.id)"
        >
          <mat-icon aria-hidden="true">{{ tab.icon }}</mat-icon>
          <span>{{ tab.labelKey | translate }}</span>
        </button>
      }
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>
      @if (activeTab() === 'daten') {
        <section class="user-invite-page__panel" aria-labelledby="user-invite-section-title">
          <div class="user-invite-page__section-title">
            <h2 id="user-invite-section-title">{{ 'userInvite.stammdaten.title' | translate }}</h2>
            <p>{{ 'userInvite.stammdaten.subtitle' | translate }}</p>
          </div>

          <div class="user-invite-page__form-grid">
            <mat-form-field appearance="fill" class="user-invite-page__form-field">
              <mat-label>{{ 'userInvite.stammdaten.firstNameLabel' | translate }}</mat-label>
              <input
                matInput
                formControlName="firstName"
                [attr.maxlength]="fieldLengths.FIRST_LAST_NAME_MAX"
                required
              />
              @if (form.controls.firstName.hasError('required') && form.controls.firstName.touched) {
                <mat-error>{{ 'userInvite.stammdaten.firstNameRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-invite-page__form-field">
              <mat-label>{{ 'userInvite.stammdaten.lastNameLabel' | translate }}</mat-label>
              <input matInput formControlName="lastName" [attr.maxlength]="fieldLengths.FIRST_LAST_NAME_MAX" required />
              @if (form.controls.lastName.hasError('required') && form.controls.lastName.touched) {
                <mat-error>{{ 'userInvite.stammdaten.lastNameRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-invite-page__form-field user-invite-page__form-field--full">
              <mat-label>{{ 'userInvite.stammdaten.emailLabel' | translate }}</mat-label>
              <input
                matInput
                type="email"
                formControlName="email"
                [attr.maxlength]="fieldLengths.EMAIL_MAX"
                [placeholder]="'userInvite.stammdaten.emailPlaceholder' | translate"
                required
              />
              @if (form.controls.email.hasError('duplicate')) {
                <mat-error>{{ 'userInvite.duplicateEmail' | translate }}</mat-error>
              } @else if (form.controls.email.hasError('required')) {
                <mat-error data-testid="email-required-error">
                  {{ 'userInvite.stammdaten.emailRequired' | translate }}
                </mat-error>
              } @else if (form.controls.email.hasError('email')) {
                <mat-error>{{ 'userInvite.stammdaten.emailInvalid' | translate }}</mat-error>
              } @else if (form.controls.email.hasError('domainNotAllowed')) {
                <mat-error data-testid="email-domain-error">
                  @if (allowedDomainsLabel()) {
                    {{ 'userInvite.domainNotAllowed' | translate: { domains: allowedDomainsLabel() } }}
                  } @else {
                    {{ 'userInvite.domainNoneConfigured' | translate }}
                  }
                </mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-invite-page__form-field">
              <mat-label>{{ 'userInvite.stammdaten.roleLabel' | translate }}</mat-label>
              <mat-select formControlName="roleId" [placeholder]="'userInvite.stammdaten.rolePlaceholder' | translate">
                @for (role of invitableRoles(); track role.id) {
                  <mat-option [value]="role.id">{{ role.displayName }}</mat-option>
                }
              </mat-select>
              @if (form.controls.roleId.hasError('required')) {
                <mat-error data-testid="role-required-error">
                  {{ 'userInvite.stammdaten.roleRequired' | translate }}
                </mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-invite-page__form-field user-invite-page__status-field">
              <mat-label>{{ 'userInvite.stammdaten.statusLabel' | translate }}</mat-label>
              <mat-select data-testid="invite-status" [value]="'invite'" disabled>
                <mat-option [value]="'invite'">{{ statusDisplay | translate }}</mat-option>
              </mat-select>
              <mat-hint>{{ 'userInvite.stammdaten.statusHint' | translate }}</mat-hint>
            </mat-form-field>
          </div>
        </section>
      }

      @if (activeTab() === 'rolle') {
        <section class="user-invite-page__panel">
          <div class="user-invite-page__section-title">
            <h2>{{ 'userInvite.tabs.berechtigungen' | translate }}</h2>
            <p>{{ 'userInvite.berechtigungen.subtitle' | translate }}</p>
          </div>

          <div class="user-invite-page__info-banner" role="note">
            <mat-icon aria-hidden="true">shield</mat-icon>
            <span>
              {{ 'userInvite.berechtigungen.rolePrefix' | translate }}
              <strong>{{ selectedRoleLabel() }}</strong>
              {{ 'userInvite.berechtigungen.roleSuffix' | translate }}
            </span>
          </div>

          <div class="user-invite-page__perm-sections">
            @for (section of permissionSections; track section.id) {
              <div class="user-invite-page__perm-section">
                <header class="user-invite-page__perm-header">
                  <span class="user-invite-page__perm-icon" aria-hidden="true">
                    <mat-icon>{{ section.icon }}</mat-icon>
                  </span>
                  <h3 class="user-invite-page__perm-label">{{ section.labelKey | translate }}</h3>
                </header>
                <ul class="user-invite-page__perm-list">
                  @for (item of section.items; track item.key) {
                    <li class="user-invite-page__perm-item">
                      <mat-checkbox
                        [checked]="isPermissionChecked(item.key)"
                        (change)="togglePermission(item.key, $event.checked)"
                      >
                        {{ item.labelKey | translate }}
                      </mat-checkbox>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        </section>
      }

      @if (activeTab() === 'praeferenzen') {
        <section class="user-invite-page__panel">
          <div class="user-invite-page__section-title">
            <h2>{{ 'userInvite.tabs.praeferenzen' | translate }}</h2>
            <p>{{ 'userInvite.praeferenzen.subtitle' | translate }}</p>
          </div>

          <div class="user-invite-page__form-grid">
            <mat-form-field appearance="fill" class="user-invite-page__form-field user-invite-page__pref-field">
              <mat-label>{{ 'userInvite.praeferenzen.languageLabel' | translate }}</mat-label>
              <mat-select formControlName="language">
                @for (lang of languageOptions; track lang.code) {
                  <mat-option [value]="lang.code">{{ lang.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-invite-page__form-field user-invite-page__pref-field">
              <mat-label>{{ 'userInvite.praeferenzen.timezoneLabel' | translate }}</mat-label>
              <input
                matInput
                type="search"
                [ngModel]="timezoneSearch()"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onTimezoneInput($event)"
                (blur)="onTimezoneBlur()"
                [matAutocomplete]="timezoneAuto"
                [attr.aria-label]="'userInvite.praeferenzen.timezoneLabel' | translate"
                [placeholder]="'userInvite.praeferenzen.timezonePlaceholder' | translate"
              />
              <button
                type="button"
                matSuffix
                mat-icon-button
                class="user-invite-page__tz-toggle"
                data-testid="timezone-trigger"
                tabindex="-1"
                [attr.aria-label]="'userInvite.praeferenzen.timezoneToggle' | translate"
                (click)="openTimezonePanel()"
              >
                <mat-icon aria-hidden="true">arrow_drop_down</mat-icon>
              </button>
              <mat-autocomplete
                #timezoneAuto="matAutocomplete"
                (optionSelected)="onTimezoneSelected($event.option.value)"
              >
                @for (tz of filteredTimezones(); track tz) {
                  <mat-option [value]="tz">{{ timezoneLabel(tz) }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          </div>
        </section>
      }
    </form>
  </div>
</section>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'common.unsavedChanges.message' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
